import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import styled from "styled-components";

import AiCursor from "components/atoms/AiCursor";
import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import { CountTagItem, TagText } from "components/atoms/Tag";
import Textarea from "components/atoms/Textarea";
import Tooltip from "components/atoms/Tooltip";
import Confirm from "components/atoms/alert/Confirm";
import Button, { BUTTON_STYLES } from "components/atoms/button";
import Text from "components/atoms/text/Text";

import { BodyWithBreadcrumb, Breadcrumb } from "components/molecules/Breadcrumb";
import ReferenceDetailTitleInput from "components/organisms/reference/ReferenceDetailTitleInput";
import PDFChatPageLayout from "components/organisms/review/PDFChatPageLayout";
import PDFChatSidebar from "components/organisms/review/PDFChatSidebar";
import ReferenceBibInfoEditModal from "components/page/modal/ReferenceBibInfoEditModal";
import ReferenceCitationEditModal from "components/page/modal/ReferenceCitationEditModal";
import ReferenceCitationStyleModal from "components/page/modal/ReferenceCitationStyleModal";
import ReferenceFolderEditModal from "components/page/modal/ReferenceFolderEditModal";
import ReferenceTagEditModal from "components/page/modal/ReferenceTagEditModal";
import LoginUserLayout from "components/templates/LoginUserLayout";

import {
    useBibliographies,
    useBibliography,
    useBibliographyDelete,
    useBibliographyUpdate,
    useFolders,
} from "hooks/queries/useBibliographies";
import { useUser } from "hooks/queries/useUser";
import { usePDFChatWebSocket } from "hooks/usePDFChatWebSocket";
import { useSessionPageHistory } from "hooks/useSessionPageHistory";

import Constants from "utils/constants";
import { getDateFromString, getTimeFromString } from "utils/dateUtil";
import { isAdminUser, isReadonlyUser } from "utils/user";
import { genericEnterKeyDown } from "utils/validation";

const DEFAULT_REFERENCE_LIST_PARAMS = {
    page: 1,
    page_size: 100,
    ordering: "-created_at",
};

const HeaderLeftSec = styled.div`
    display: flex;
    align-items: center;
    column-gap: 8px;
    width: 100%;
    max-width: calc(100% - 60px);
`;

const HeaderRightSec = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 52px;
    margin: 0 0 0 8px;
`;

const MainContainer = styled.div`
    max-width: ${Constants.TABLET_WIDTH}px;
    margin-bottom: 96px;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    height: 20px;
    gap: 4px;
    margin-bottom: 8px;
`;

const SectionHeaderTitle = styled(Text)`
    font-size: 1.4rem;
    font-weight: 500;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const BibInfoSection = styled.div`
    margin-bottom: 40px;
`;

const BibInfoText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Grey1)",
})``;

const AbstractSection = styled.div`
    flex: 1;
    margin-top: 16px;
    margin-bottom: 40px;
    min-height: 32px;
`;

const AbstractTextarea = styled(Textarea)``;

const AnnotaionSection = styled.div`
    display: flex;
    margin-bottom: 40px;
`;

const AnnotationLeftSection = styled.div`
    width: 100%;
    max-width: ${Constants.TABLET_WIDTH / 2}px;
`;

const ReferenceLinkItem = styled.div`
    width: 100%;
    margin-bottom: 16px;
`;

const ReferenceLinkItemHeader = styled(SectionHeader)``;

const ReferenceLinkItemHeaderTitle = styled(Text)`
    font-size: 1.4rem;
    font-weight: 500;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const ReferenceLinkTagItemWrap = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: auto;
    min-height: 20px;
`;

const ReferenceLinkTagItem = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: ${(props) => props.$margin || "0"};
    padding: 0px 8px;
    height: 20px;
    outline: none;
    background-color: var(--color-Tag);
    border: transparent;
    border-radius: 4px;
`;

const ReferenceLinkItemBlankBox = styled.div`
    display: flex;
    justify-content: start;
    align-items: center;
    height: 20px;
`;

const ReferenceLinkItemBlankText = styled(Text)`
    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const AnnotationRightSection = styled.div`
    width: 100%;
    max-width: ${Constants.TABLET_WIDTH / 2}px;

    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const MemoSection = styled.div`
    flex: 1;

    display: flex;
    flex-direction: column;
`;

const MemoTitleText = styled(Text).attrs((props) => ({
    variant: "body-md-500",
}))`
    margin: 0 0 8px 0;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const MemoDiv = styled.div`
    flex: 1;
`;

const MemoTextarea = styled.textarea`
    padding: 4px 6px;
    width: 100%;
    min-height: 100%;
    font-size: 1.2rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    background-color: var(--color-White);
    border: solid 1px #ecf2f4;
    border-radius: 2px;
    resize: none;
    outline: none;

    &::placeholder {
        color: var(--color-Grey2);
    }

    &:disabled {
        background-color: var(--color-DisabledInput);
        cursor: not-allowed;
    }
`;

const DateSection = styled.div`
    flex: 0 0 auto;

    display: flex;
    justify-content: end;
    align-items: center;
    gap: 8px;
`;

const DateText = styled(Text).attrs((props) => ({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
}))``;

const ReferenceListSection = styled.div`
    width: 100%;
`;

const ReferenceListHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 20px;
    margin-bottom: 8px;
`;

const ReferenceListTitleText = styled(SectionHeaderTitle)``;

const ReferenceListParamsWrap = styled.div`
    display: flex;
    align-items: end;
    flex-wrap: wrap;
    margin-bottom: 8px;
`;

const ReferenceFilterTagItem = styled(ReferenceLinkTagItem)`
    transition: all 120ms ease-in;
    background-color: var(--color-Tag);
    cursor: pointer;

    &.active {
        background-color: var(--color-TagActive);
    }
`;

const ReferenceListContent = styled.div``;

const ReferenceListBlankContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-top: 80px;
`;

const ReferenceListBlankText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-SubBlack)",
})``;

const ReferenceListItemRow = styled.div`
    padding: 8px 0px;

    border-bottom: solid 1px var(--color-Background);
    cursor: default;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        transition: all 80ms ease-in;
        cursor: pointer;
        background-color: var(--color-ListHover);
    }
`;

const ReferenceListItemTitle = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-SubBlack)",
})``;

const ReferenceListItemText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})``;

function ReferencePage(props) {
    const { space_uuid, reference_no } = useParams();
    const navigate = useNavigate();
    const openNewTab = (path) => window.open(`/${space_uuid}${path}`, "_blank", "noopener noreferrer");
    const referenceNo = Number(reference_no);

    // 사용자 처리
    const [user, setUser] = useState({});
    const userQuery = useUser();

    useEffect(() => {
        if (userQuery.data) {
            setUser(userQuery.data);
        }
    }, [userQuery.data]);

    // 레퍼런스 처리
    const [reference, setReference] = useState({});
    const referenceQuery = useBibliography({ reference_no: referenceNo });
    const { data: referenceData, refetch: refetchReference, remove: removeReference } = referenceQuery;
    useEffect(() => {
        if (referenceData) {
            setReference(referenceData);
        }
    }, [referenceData]);

    useEffect(() => {
        if (!Number.isInteger(referenceNo) || referenceNo < 1) {
            navigate("/error", { replace: true });
        }
    }, [referenceNo, navigate]);

    useEffect(() => {
        return () => {
            removeReference();
        };
    }, [removeReference]);

    // region: 목록 - 상세 네비게이션
    const [searchParams, setSearchParams] = useState();
    const { prevPage } = useSessionPageHistory();

    const loadSessionCache = () => {
        const sessionCacheJson = sessionStorage.getItem("referenceList");
        if (sessionCacheJson) {
            const sessionCacheObj = JSON.parse(sessionCacheJson);
            setSearchParams(sessionCacheObj.searchParams);
        }
    };

    useEffect(() => {
        if (prevPage && prevPage.startsWith(`/${space_uuid}/reference`)) {
            loadSessionCache();
        }
    }, [prevPage, space_uuid]);

    // region: 웹소켓 훅
    const {
        threads,
        selectedThread,
        setSelectedThread,
        profiles,
        streamingMessageIds,
        autoScrollToEnd,
        setAutoScrollToEnd,
        pdfIndexingInfo,
        chatStart,
        chatStop,
        chatDelete,
        threadUpdateTitle,
        myProfileId,
    } = usePDFChatWebSocket({
        reference_uuid: reference.uuid,
        user,
        connectionDependencies: [reference.uuid, userQuery.data],
        onFileUploaded: () => {
            refetchReference();
        },
        onFileDeleted: () => {
            refetchReference();
        },
        onPdfMetadata: () => {
            refetchReference();
        },
        onStreamStart: (streaming_message_id) => {
            setFocusedMessageId(streaming_message_id);
        },
    });

    const [focusedMessageId, setFocusedMessageId] = useState(null);

    // 서지정보
    const abstractTextareaRef = useRef(null);

    const updateBibliographyMutation = useBibliographyUpdate();
    const deleteBibliographyMutation = useBibliographyDelete();
    const handleReferenceFieldChanged = (event) => {
        const { name, value } = event.target;
        // bib_json 내부 필드는 "bib_json.필드명" 형태로 name을 넣어줄 수 있음
        if (name.startsWith("bib_json.")) {
            setReference((prev) => ({
                ...prev,
                bib_json: {
                    ...prev.bib_json,
                    [name.replace("bib_json.", "")]: value,
                },
            }));
        } else {
            setReference((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };
    const updateReferenceField = (fields) => {
        if (!reference.id) return;
        const updateData = { id: reference.id };

        Object.entries(fields).forEach(([key, value]) => {
            // bib_json.* 필드는 bib_json 객체 내부로 병합
            if (key.startsWith("bib_json.")) {
                if (!updateData.bib_json) updateData.bib_json = { ...reference.bib_json };
                updateData.bib_json[key.replace("bib_json.", "")] = value;
            } else {
                updateData[key] = value;
            }
        });

        updateBibliographyMutation.mutate(updateData);
    };

    // 참고 문헌 삭제
    const handleDeleteButtonClick = () => {
        let message = "참고문헌을 삭제하시겠습니까?";
        if (reference.citation_count > 0) {
            message = "연구노트에 인용된 참고문헌입니다. 삭제하시겠습니까?";
        }
        Confirm("warn", "참고문헌 삭제", message, "삭제", (e) => {
            deleteBibliographyMutation.mutate({
                ids: [reference.id],
            });
            navigate(-1);
        });
    };

    // 폴더
    const folderQuery = useFolders();
    const [referenceFolder, setReferenceFolder] = useState(null);
    useEffect(() => {
        if (folderQuery.data || reference.folder) {
            // 폴더 목록에서 현재 참고문헌이 포함된 폴더를 찾음
            setReferenceFolder(folderQuery.data.find((folder) => folder.id === reference.folder));
        }
    }, [folderQuery.data, reference.folder]);

    // 메모
    const memoTextareaRef = useRef();

    useEffect(() => {
        const textarea = memoTextareaRef.current;
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
    }, [reference?.memo]);

    // 모달
    const [showCitationStyleModal, setShowCitationStyleModal] = useState(false);
    const [showFolderEditModal, setShowFolderEditModal] = useState(false);
    const [showTagEditModal, setShowTagEditModal] = useState(false);
    const [showCitationEditModal, setShowCitationEditModal] = useState(false);
    const [showBibInfoEditModal, setShowBibInfoEditModal] = useState(false);

    // region: 연결된 참고문헌
    const [referenceList, setReferenceList] = useState([]);
    const [referenceListParams, setReferenceListParams] = useState(null);
    const referenceListQuery = useBibliographies(referenceListParams);
    const { data: referenceListData, refetch: refetchReferenceList } = referenceListQuery;

    useEffect(() => {
        if (referenceData && referenceListParams === null) {
            const newParams = {
                ...DEFAULT_REFERENCE_LIST_PARAMS,
                folder: referenceData.folder,
                tags: referenceData.tag_mappings.map((tag) => tag.id).join(","),
                researches: referenceData.researches.map((research) => research.id).join(","),
            };
            setReferenceListParams(newParams);
        }
    }, [referenceData, referenceListParams]);

    useEffect(() => {
        if (referenceListParams) {
            refetchReferenceList();
        }
    }, [referenceListParams, refetchReferenceList]);

    useEffect(() => {
        if (referenceListData) {
            setReferenceList(referenceListData.results);
        }
    }, [referenceListData]);

    const referenceListParamsRenderer = () => {
        const paramELs = [];
        // 1. 폴더 표시
        if (referenceList && referenceListParams && referenceFolder) {
            const is_active = referenceFolder.id === referenceListParams.folder;
            paramELs.push(
                <Tooltip key="folder" message={is_active ? "클릭하여 필터 해제" : "클릭하여 필터 적용"}>
                    <ReferenceFilterTagItem
                        className={is_active ? "active" : ""}
                        $margin={"0 8px 2px 0"}
                        onClick={() => {
                            if (is_active) {
                                setReferenceListParams((prev) => ({
                                    ...prev,
                                    folder: null,
                                }));
                            } else {
                                if (referenceListParams.folder === null) {
                                    setReferenceListParams((prev) => ({
                                        ...prev,
                                        folder: referenceFolder.id,
                                    }));
                                }
                            }
                        }}
                    >
                        <Icon name="folder" size="8" color="var(--color-SubBlack)" />
                        <TagText style={{ margin: "0 8px 0 8px" }} ellipsis>
                            {referenceFolder.name} (
                            {referenceList.filter((r) => r.folder === referenceFolder.id).length})
                        </TagText>
                        <Icon name={is_active ? "x" : "plus"} size="8" color="var(--color-SubBlack)" />
                    </ReferenceFilterTagItem>
                </Tooltip>,
            );
        }
        // 2. 태그 표시
        if (referenceList && referenceListParams && reference.tag_mappings) {
            const tags = referenceListParams.tags
                .split(",")
                .filter((tag_id) => !!tag_id)
                .map((tag_id) => reference.tag_mappings.find((item) => item.id === Number(tag_id)))
                .filter(Boolean); // undefined 필터

            paramELs.push(
                ...reference.tag_mappings.map((tag, index) => {
                    const tag_ids = tags.map((t) => t.id);
                    const is_active = tag_ids.includes(tag.id);

                    return (
                        <Tooltip
                            key={index + "TAG_FILTER"}
                            message={is_active ? "클릭하여 필터 해제" : "클릭하여 필터 적용"}
                        >
                            <ReferenceFilterTagItem
                                className={is_active ? "active" : ""}
                                $margin={"0 8px 2px 0"}
                                onClick={(e) => {
                                    if (is_active) {
                                        setReferenceListParams((prev) => ({
                                            ...prev,
                                            tags: prev.tags
                                                .split(",")
                                                .filter((id) => Number(id) !== tag.id)
                                                .join(","),
                                        }));
                                    } else {
                                        tag_ids.push(tag.id);
                                        setReferenceListParams((prev) => ({
                                            ...prev,
                                            tags: tag_ids.join(","),
                                        }));
                                    }
                                }}
                            >
                                <Icon name="tag" size="8" color="var(--color-SubBlack)" />
                                <TagText style={{ margin: "0 8px 0 8px" }} ellipsis>
                                    {tag.name} (
                                    {
                                        referenceList.filter((r) => r.tag_mappings.map((t) => t.id).includes(tag.id))
                                            .length
                                    }
                                    )
                                </TagText>
                                <Icon name={is_active ? "x" : "plus"} size="8" color="var(--color-SubBlack)" />
                            </ReferenceFilterTagItem>
                        </Tooltip>
                    );
                }),
            );
        }
        // 3. 연구노트 표시
        if (referenceList && referenceListParams && reference.researches) {
            const researches = referenceListParams.researches
                .split(",")
                .filter((research_id) => !!research_id)
                .map((research_id) => reference.researches.find((item) => item.id === Number(research_id)))
                .filter(Boolean); // undefined 필터

            paramELs.push(
                ...reference.researches.map((research, index) => {
                    const research_ids = researches.map((r) => r.id);
                    const is_active = research_ids.includes(research.id);

                    return (
                        <Tooltip
                            key={index + "RESEARCH_FILTER"}
                            message={is_active ? "클릭하여 필터 해제" : "클릭하여 필터 적용"}
                        >
                            <ReferenceFilterTagItem
                                className={is_active ? "active" : ""}
                                $margin={"0 8px 2px 0"}
                                onClick={(e) => {
                                    if (is_active) {
                                        setReferenceListParams((prev) => ({
                                            ...prev,
                                            researches: prev.researches
                                                .split(",")
                                                .filter((id) => Number(id) !== research.id)
                                                .join(","),
                                        }));
                                    } else {
                                        research_ids.push(String(research.id));
                                        setReferenceListParams((prev) => ({
                                            ...prev,
                                            researches: research_ids.join(","),
                                        }));
                                    }
                                }}
                            >
                                <Icon name="tag" size="8" color="var(--color-SubBlack)" />
                                <TagText style={{ margin: "0 8px 0 8px" }} ellipsis>
                                    {research.display_name} (
                                    {
                                        referenceList.filter((r) => r.researches.map((t) => t.id).includes(research.id))
                                            .length
                                    }
                                    )
                                </TagText>
                                <Icon name={is_active ? "x" : "plus"} size="8" color="var(--color-SubBlack)" />
                            </ReferenceFilterTagItem>
                        </Tooltip>
                    );
                }),
            );
        }
        return paramELs;
    };

    // region: 마크업
    return (
        <LoginUserLayout searchParams={searchParams}>
            <HtmlHead title={reference?.display_name} />
            <PDFChatPageLayout
                main={
                    <>
                        <Breadcrumb style={{ maxWidth: "864px" }}>
                            <HeaderLeftSec>
                                <Tooltip message={"뒤로"}>
                                    <Button
                                        {...BUTTON_STYLES.iconSecondary}
                                        onClick={() => {
                                            navigate(-1);
                                        }}
                                        disabled={window.history.length === 1}
                                    >
                                        <Icon name="arrowLeft" size="12px" />
                                    </Button>
                                </Tooltip>
                                {/* 참고문헌 표시 이름 */}
                                <ReferenceDetailTitleInput
                                    name={"display_name"}
                                    placeholder={"참고문헌 이름을 입력해주세요."}
                                    value={reference?.display_name}
                                    allowEmpty={false}
                                    onChange={handleReferenceFieldChanged}
                                    saveAct={(e) => updateReferenceField({ display_name: reference?.display_name })}
                                />
                            </HeaderLeftSec>
                            <HeaderRightSec>
                                {/* 북마크 버튼 */}
                                <Tooltip message={"즐겨찾기"}>
                                    <Button
                                        {...BUTTON_STYLES.iconSurfaceStatic}
                                        border={
                                            reference?.is_favorite
                                                ? "solid 2px var(--color-Key)"
                                                : "solid 2px var(--color-DisabledButton)"
                                        }
                                        onClick={(e) => updateReferenceField({ is_favorite: !reference.is_favorite })}
                                        disabled={isReadonlyUser(user)}
                                    >
                                        <Icon
                                            name={"bookmark"}
                                            size={"12"}
                                            color={
                                                reference?.is_favorite
                                                    ? "var(--color-Key)"
                                                    : "var(--color-DisabledButton)"
                                            }
                                        />
                                    </Button>
                                </Tooltip>

                                {/* 참고문헌 삭제 버튼 */}
                                <Tooltip message={"참고문헌 삭제"}>
                                    <Button
                                        onlyIcon
                                        {...BUTTON_STYLES.actionPrimary}
                                        onClick={handleDeleteButtonClick}
                                        disabled={isReadonlyUser(user) && !isAdminUser(user)}
                                    >
                                        <Icon name={"delete"} size={"12"} color={"var(--color-White)"} />
                                    </Button>
                                </Tooltip>
                            </HeaderRightSec>
                        </Breadcrumb>

                        <BodyWithBreadcrumb>
                            <MainContainer>
                                {/* 서지정보 */}
                                <BibInfoSection>
                                    <SectionHeader>
                                        <SectionHeaderTitle>서지정보</SectionHeaderTitle>
                                        <Tooltip message={"서지정보 수정"}>
                                            <Button
                                                {...BUTTON_STYLES.iconGhost}
                                                buttonSize={"20px"}
                                                onClick={() => setShowBibInfoEditModal(true)}
                                                disabled={isReadonlyUser(user)}
                                            >
                                                <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                                            </Button>
                                        </Tooltip>

                                        <Tooltip message={"인용 스타일"}>
                                            <Button
                                                {...BUTTON_STYLES.iconGhost}
                                                buttonSize={"20px"}
                                                onClick={() => setShowCitationStyleModal(true)}
                                            >
                                                <Icon name={"list"} color={"var(--color-SubBlack)"} />
                                            </Button>
                                        </Tooltip>
                                    </SectionHeader>
                                    <BibInfoText>{reference?.styled_text || ""}</BibInfoText>
                                </BibInfoSection>

                                {/* 초록 */}
                                <AbstractSection>
                                    <SectionHeader>
                                        <SectionHeaderTitle>초록</SectionHeaderTitle>
                                    </SectionHeader>
                                    {reference?.file &&
                                    reference?.file_status === "DONE" &&
                                    !reference?.bib_json?.abstract ? (
                                        <Tooltip message={"PDF 파일에서 추출 중입니다."}>
                                            <AiCursor />
                                        </Tooltip>
                                    ) : (
                                        <AbstractTextarea
                                            ref={abstractTextareaRef}
                                            placeholder={"PDF 파일을 업로드하여 자동으로 추출할 수 있습니다."}
                                            name={"bib_json.abstract"}
                                            value={reference?.bib_json?.abstract}
                                            onChange={handleReferenceFieldChanged}
                                            saveAct={(e) =>
                                                updateReferenceField({
                                                    "bib_json.abstract": reference?.bib_json?.abstract,
                                                })
                                            }
                                            disabled={isReadonlyUser(user)}
                                            padding={"2px"}
                                        />
                                    )}
                                </AbstractSection>

                                <AnnotaionSection>
                                    <AnnotationLeftSection>
                                        {/* 폴더 */}
                                        <ReferenceLinkItem>
                                            <ReferenceLinkItemHeader>
                                                <ReferenceLinkItemHeaderTitle>폴더</ReferenceLinkItemHeaderTitle>
                                                <Tooltip message={"폴더 연결"}>
                                                    <Button
                                                        {...BUTTON_STYLES.iconGhost}
                                                        buttonSize={"20px"}
                                                        onClick={() => setShowFolderEditModal(true)}
                                                        disabled={isReadonlyUser(user)}
                                                    >
                                                        <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                                                    </Button>
                                                </Tooltip>
                                            </ReferenceLinkItemHeader>
                                            {referenceFolder && referenceList ? (
                                                <ReferenceLinkTagItemWrap>
                                                    <ReferenceLinkTagItem $margin={"0 8px 2px 0"}>
                                                        <TagText ellipsis>{referenceFolder.name}</TagText>
                                                    </ReferenceLinkTagItem>
                                                </ReferenceLinkTagItemWrap>
                                            ) : (
                                                <ReferenceLinkItemBlankBox>
                                                    <ReferenceLinkItemBlankText>
                                                        연결된 폴더가 없습니다.
                                                    </ReferenceLinkItemBlankText>
                                                </ReferenceLinkItemBlankBox>
                                            )}
                                        </ReferenceLinkItem>

                                        {/* 태그 */}
                                        <ReferenceLinkItem>
                                            <ReferenceLinkItemHeader>
                                                <ReferenceLinkItemHeaderTitle>태그</ReferenceLinkItemHeaderTitle>
                                                <Tooltip message={"태그 연결"}>
                                                    <Button
                                                        {...BUTTON_STYLES.iconGhost}
                                                        buttonSize={"20px"}
                                                        onClick={() => setShowTagEditModal(true)}
                                                        disabled={isReadonlyUser(user)}
                                                    >
                                                        <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                                                    </Button>
                                                </Tooltip>
                                            </ReferenceLinkItemHeader>
                                            {reference?.tag_mappings?.length > 0 && referenceList ? (
                                                <ReferenceLinkTagItemWrap>
                                                    {reference.tag_mappings.map((tag, index) => (
                                                        <ReferenceLinkTagItem
                                                            key={index + "TAG"}
                                                            $margin={"0 8px 2px 0"}
                                                        >
                                                            <TagText ellipsis>{tag.name}</TagText>
                                                        </ReferenceLinkTagItem>
                                                    ))}
                                                </ReferenceLinkTagItemWrap>
                                            ) : (
                                                <ReferenceLinkItemBlankBox>
                                                    <ReferenceLinkItemBlankText>
                                                        연결된 태그가 없습니다.
                                                    </ReferenceLinkItemBlankText>
                                                </ReferenceLinkItemBlankBox>
                                            )}
                                        </ReferenceLinkItem>

                                        {/* 연구노트 */}
                                        <ReferenceLinkItem>
                                            <ReferenceLinkItemHeader>
                                                <ReferenceLinkItemHeaderTitle>연구노트</ReferenceLinkItemHeaderTitle>
                                                <Tooltip message={"연구노트 연결"}>
                                                    <Button
                                                        {...BUTTON_STYLES.iconGhost}
                                                        buttonSize={"20px"}
                                                        onClick={() => setShowCitationEditModal(true)}
                                                        disabled={isReadonlyUser(user)}
                                                    >
                                                        <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                                                    </Button>
                                                </Tooltip>
                                            </ReferenceLinkItemHeader>
                                            {reference?.researches?.length > 0 && referenceList ? (
                                                <ReferenceLinkTagItemWrap>
                                                    {reference.researches.map((research, index) => (
                                                        <ReferenceLinkTagItem
                                                            key={index + "RESEARCH"}
                                                            $margin={"0 8px 2px 0"}
                                                        >
                                                            <TagText ellipsis>{research.display_name}</TagText>
                                                        </ReferenceLinkTagItem>
                                                    ))}
                                                </ReferenceLinkTagItemWrap>
                                            ) : (
                                                <ReferenceLinkItemBlankBox>
                                                    <ReferenceLinkItemBlankText>
                                                        연결된 연구노트가 없습니다.
                                                    </ReferenceLinkItemBlankText>
                                                </ReferenceLinkItemBlankBox>
                                            )}
                                        </ReferenceLinkItem>
                                    </AnnotationLeftSection>

                                    <AnnotationRightSection>
                                        {/* 메모 */}
                                        <MemoSection>
                                            <MemoTitleText>메모</MemoTitleText>
                                            <MemoDiv>
                                                <MemoTextarea
                                                    ref={memoTextareaRef}
                                                    rows={1}
                                                    name={"memo"}
                                                    placeholder={"입력된 메모가 없습니다."}
                                                    value={reference?.memo || ""}
                                                    onChange={handleReferenceFieldChanged}
                                                    onKeyDown={(e) => {
                                                        genericEnterKeyDown(e, () => {
                                                            if (e.shiftKey) {
                                                                handleReferenceFieldChanged(e);
                                                            } else {
                                                                //blur 처리
                                                                e.preventDefault();
                                                                memoTextareaRef.current.blur();
                                                            }
                                                        });
                                                    }}
                                                    onBlur={(e) => {
                                                        updateReferenceField({ memo: reference.memo });
                                                    }}
                                                    disabled={isReadonlyUser(user)}
                                                />
                                            </MemoDiv>
                                        </MemoSection>
                                        {/* 추가 / 최근 활동 날짜 */}
                                        <DateSection>
                                            <DateText>
                                                추가 {getDateFromString(reference.created_at)}{" "}
                                                {getTimeFromString(reference.created_at)}
                                            </DateText>
                                            <DateText>|</DateText>
                                            <DateText>
                                                최근 활동 {getDateFromString(reference.updated_at)}{" "}
                                                {getTimeFromString(reference.updated_at)}
                                            </DateText>
                                        </DateSection>
                                    </AnnotationRightSection>
                                </AnnotaionSection>

                                <ReferenceListSection>
                                    <ReferenceListHeader>
                                        <ReferenceListTitleText>연결된 참고문헌</ReferenceListTitleText>
                                        <CountTagItem>
                                            <TagText>
                                                {referenceListParams?.folder === null &&
                                                referenceListParams?.tags === "" &&
                                                referenceListParams?.researches === ""
                                                    ? 0
                                                    : referenceList?.length}{" "}
                                                건
                                            </TagText>
                                        </CountTagItem>
                                    </ReferenceListHeader>
                                    <ReferenceListParamsWrap>{referenceListParamsRenderer()}</ReferenceListParamsWrap>
                                    <ReferenceListContent>
                                        {referenceList &&
                                            (referenceList.length === 0 ||
                                            (referenceListParams?.folder === null &&
                                                referenceListParams?.tags === "" &&
                                                referenceListParams?.researches === "") ? (
                                                <ReferenceListBlankContainer>
                                                    <Icon name={"folder"} size={"32"} color={"var(--color-Grey1)"} />
                                                    <ReferenceListBlankText>
                                                        연결된 참고문헌이 없습니다.
                                                    </ReferenceListBlankText>
                                                </ReferenceListBlankContainer>
                                            ) : (
                                                referenceList.map((item, idx) => (
                                                    <ReferenceListItemRow
                                                        key={idx}
                                                        onClick={(e) => {
                                                            openNewTab(`/reference/${item.reference_no}`);
                                                        }}
                                                    >
                                                        <ReferenceListItemTitle>
                                                            {item.display_name}
                                                        </ReferenceListItemTitle>
                                                        <ReferenceListItemText>
                                                            {item.styled_text}
                                                        </ReferenceListItemText>
                                                    </ReferenceListItemRow>
                                                ))
                                            ))}
                                    </ReferenceListContent>
                                </ReferenceListSection>
                            </MainContainer>
                        </BodyWithBreadcrumb>
                    </>
                }
                sidebar={
                    <PDFChatSidebar
                        user={user}
                        myProfileId={myProfileId}
                        reference={reference}
                        threads={threads}
                        selectedThread={selectedThread}
                        setSelectedThread={setSelectedThread}
                        profiles={profiles}
                        chatStart={chatStart}
                        chatStop={chatStop}
                        chatDelete={chatDelete}
                        threadUpdateTitle={threadUpdateTitle}
                        streamingMessageIds={streamingMessageIds}
                        autoScrollToEnd={autoScrollToEnd}
                        setAutoScrollToEnd={setAutoScrollToEnd}
                        pdfIndexingInfo={pdfIndexingInfo}
                        focusedMessageId={focusedMessageId}
                    />
                }
            />
            {showCitationStyleModal && (
                <ReferenceCitationStyleModal
                    exitModal={(e) => setShowCitationStyleModal(false)}
                    reference={reference}
                />
            )}
            {showCitationEditModal && (
                <ReferenceCitationEditModal
                    exitModal={(e) => setShowCitationEditModal(!showCitationEditModal)}
                    citedResearches={reference.researches}
                    referenceId={reference?.id}
                />
            )}
            {showFolderEditModal && (
                <ReferenceFolderEditModal
                    referenceIds={[reference.id]}
                    selectedFolderId={reference.folder}
                    exitModal={() => setShowFolderEditModal(false)}
                />
            )}
            {showTagEditModal && (
                <ReferenceTagEditModal
                    referenceId={reference.id}
                    referenceFile={reference.file}
                    selectedTagIds={reference.tag_mappings.map((tag) => tag.id)}
                    exitModal={() => {
                        setShowTagEditModal(false);
                    }}
                />
            )}
            {showBibInfoEditModal && (
                <ReferenceBibInfoEditModal
                    reference={reference}
                    exitModal={(e) => {
                        setShowBibInfoEditModal(false);
                    }}
                />
            )}
        </LoginUserLayout>
    );
}

export default ReferencePage;
